import { EntityId, PayloadAction, Update, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { addFileAction, removeFileAction } from '../files/actions';
import { Change, getChangeValue } from '../../change.type';
import { FileType } from 'helpers';

export type Page = {
    readonly id: EntityId;
    readonly sourceFileId: string;
    readonly sourceFileRefId?: string | number;
    readonly pageNr: number;
    readonly type: FileType;
    index: Change<number>;
    hidden?: boolean;
    split: Change<boolean>;
    fileName: Partial<Change<string>>;
    userFileName?: string;
};

export const pagesAdapter = createEntityAdapter<Page>({
    selectId: model => model.id,
    sortComparer: (a, b) => getChangeValue(a.index) - getChangeValue(b.index)
});

export const pagesSlice = createSlice({
    name: 'pages',
    initialState: pagesAdapter.getInitialState(),
    reducers: {
        hide(state, { payload: id }: PayloadAction<EntityId>) {
            const page = state.entities[id];
            if (page) {
                page.hidden = true;
            }
        },
        show(state, { payload: id }: PayloadAction<EntityId>) {
            const page = state.entities[id];
            if (page) page.hidden = false;
        },
        split(state, { payload: id }: PayloadAction<EntityId>) {
            const page = state.entities[id];
            if (page) {
                page.split.new = !getChangeValue(page.split);
                if ((!page.split.new || page.split.old) && page.index) {
                    page.fileName.new = undefined;
                    const nextPageId = state.ids[getChangeValue(page.index)];
                    if (nextPageId) {
                        const nextPage = state.entities[nextPageId]!;
                        nextPage.fileName.new = undefined;
                    }
                }
            }
        },
        setFilename(state, { payload }: PayloadAction<Update<Page>[]>) {
            pagesAdapter.updateMany(state, payload);
        },
        splitAll(state) {
            state.ids.forEach(id => {
                const page = state.entities[id];
                if (page) {
                    if (!(page.split.new ?? page.split.old)) page.split.new = true;
                }
            });
        },
        mergeAll(state) {
            state.ids.forEach(id => {
                const page = state.entities[id];
                if (page) {
                    if (state.ids[getChangeValue(page.index)] && (page.split.new ?? page.split.old))
                        page.split.new = false;
                }
            });
        },
        restore(state) {
            const changes = state.ids.map<Update<Page>>(id => {
                const { index, fileName, split } = state.entities[id]!;
                return {
                    id,
                    changes: {
                        fileName: {
                            old: fileName.old,
                            new: undefined
                        },
                        hidden: undefined,
                        index: {
                            old: index.old,
                            new: undefined
                        },
                        split: {
                            old: split.old,
                            new: undefined
                        },
                        userFileName: undefined
                    }
                };
            });
            pagesAdapter.updateMany(state, changes);
        },
        insert(
            state,
            { payload: { pageId, insertAfterPageId } }: PayloadAction<{ pageId: EntityId; insertAfterPageId: EntityId }>
        ) {
            const prevPage = state.entities[insertAfterPageId];
            const insertedPage = state.entities[pageId];

            if (prevPage && insertedPage) {
                const startIndex = getChangeValue(insertedPage.index);
                const endIndex = getChangeValue(prevPage.index);

                if (startIndex - 1 === endIndex) return;

                const changes: Update<Page>[] = [];

                const prevPageSplit = getChangeValue(prevPage.split);
                const insertPageSplit = getChangeValue(insertedPage.split);

                if (prevPageSplit) {
                    changes.push({
                        id: prevPage.id,
                        changes: {
                            split: {
                                old: prevPage.split.old,
                                new: false
                            }
                        }
                    });
                }

                changes.push({
                    id: insertedPage.id,
                    changes: {
                        split: {
                            old: insertedPage.split.old,
                            new: prevPageSplit
                        }
                    }
                });

                if (insertPageSplit && startIndex > 1) {
                    const prevInsertedPageId = state.ids[startIndex - 2];
                    const prevInsertedPageSplit = state.entities[prevInsertedPageId]!.split;
                    if (!getChangeValue(prevInsertedPageSplit)) {
                        changes.push({
                            id: prevInsertedPageId,
                            changes: {
                                split: {
                                    old: prevInsertedPageSplit.old,
                                    new: true
                                }
                            }
                        });
                    }
                }

                if (startIndex > endIndex) {
                    changes.push({
                        id: pageId,
                        changes: { index: { old: insertedPage.index.old, new: endIndex + 1 } }
                    });
                    for (let i = endIndex + 1; i < startIndex; i++) {
                        const id = state.ids[i - 1];
                        const newIndexOfPage = state.entities[id]!.index.new;
                        const oldIndexOfPage = state.entities[id]!.index.old;
                        changes.push({
                            id,
                            changes: {
                                index: {
                                    old: oldIndexOfPage,
                                    new: (newIndexOfPage ?? oldIndexOfPage) + 1
                                }
                            }
                        });
                    }
                } else {
                    changes.push({ id: pageId, changes: { index: { old: insertedPage.index.old, new: endIndex } } });
                    for (let i = startIndex + 1; i <= endIndex; i++) {
                        const id = state.ids[i - 1];
                        const newIndexOfPage = state.entities[id]!.index.new;
                        const oldIndexOfPage = state.entities[id]!.index.old;
                        changes.push({
                            id,
                            changes: {
                                index: {
                                    old: oldIndexOfPage,
                                    new: (newIndexOfPage ?? oldIndexOfPage) - 1
                                }
                            }
                        });
                    }
                }

                pagesAdapter.updateMany(state, changes);
            }
        },
        setUserFilename(state, { payload }: PayloadAction<Update<Page>>) {
            pagesAdapter.updateOne(state, payload);
        }
    },
    extraReducers: builder => {
        builder
            .addCase(addFileAction, (state, { payload: { pages, file } }) => {
                const numOfPages = state.ids.length;
                const newPages = pages.map(value => {
                    value.index = { old: numOfPages + value.pageNr! };
                    value.split = { old: value.pageNr === pages.length };
                    // value.fileName = value.pageNr === 1 ? {old: file.fileName} : {};
                    value.fileName = { old: file.fileName };

                    return value as Page;
                });

                pagesAdapter.addMany(state, newPages);
            })
            .addCase(removeFileAction, state => pagesAdapter.removeAll(state));
    }
});
